/**
 * Menu API Service
 * 
 * Integrates with various menu API services:
 * - Yelp Fusion API
 * - Google Places API (menu data)
 * - DoorDash API (if available)
 * - Uber Eats API (if available)
 * 
 * This service provides a unified interface for fetching menu data
 * from multiple sources.
 */

import type { RestaurantMenuItem } from '../data/restaurantMenus';

export interface MenuApiResult {
  restaurantName: string;
  items: RestaurantMenuItem[];
  source: 'yelp' | 'google_places' | 'doordash' | 'ubereats' | 'backend_proxy';
  confidence: 'high' | 'medium' | 'low';
}

/**
 * Search for menu using Yelp Fusion API
 */
async function searchYelpMenu(restaurantName: string, location?: string): Promise<MenuApiResult | null> {
  const apiKey = process.env.REACT_APP_YELP_API_KEY;
  
  if (!apiKey) {
    return null;
  }

  try {
    // Search for restaurant
    const searchUrl = `https://api.yelp.com/v3/businesses/search?term=${encodeURIComponent(restaurantName)}${location ? `&location=${encodeURIComponent(location)}` : ''}`;
    
    const searchResponse = await fetch(searchUrl, {
      headers: {
        'Authorization': `Bearer ${apiKey}`,
      },
    });
    
    const searchData = await searchResponse.json();
    
    if (!searchData.businesses || searchData.businesses.length === 0) {
      return null;
    }
    
    const businessId = searchData.businesses[0].id;
    
    // Get business details (may include menu if available)
    const detailsUrl = `https://api.yelp.com/v3/businesses/${businessId}`;
    
    const detailsResponse = await fetch(detailsUrl, {
      headers: {
        'Authorization': `Bearer ${apiKey}`,
      },
    });
    
    const detailsData = await detailsResponse.json();
    
    // Yelp API doesn't directly provide menu items, but we can extract from reviews/descriptions
    // For full menu data, you'd need Yelp's Menu API (if available) or scrape their website
    
    return null; // Yelp doesn't provide menu items directly via API
  } catch (error) {
    console.error('Yelp API error:', error);
    return null;
  }
}

/**
 * Use backend proxy to scrape restaurant website
 */
async function scrapeViaBackendProxy(restaurantName: string, websiteUrl?: string): Promise<MenuApiResult | null> {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
  
  try {
    // First, try to find the restaurant's website URL
    if (!websiteUrl) {
      const findUrlResponse = await fetch(`${backendUrl}/api/find-restaurant-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ restaurantName }),
      });
      
      const findUrlData = await findUrlResponse.json();
      websiteUrl = findUrlData.websiteUrl;
    }
    
    if (!websiteUrl) {
      return null;
    }
    
    // Scrape the menu from the website
    const scrapeResponse = await fetch(`${backendUrl}/api/scrape-menu`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ restaurantName, websiteUrl }),
    });
    
    const scrapeData = await scrapeResponse.json();
    
    if (scrapeData.success && scrapeData.items && scrapeData.items.length > 0) {
      const menuItems: RestaurantMenuItem[] = scrapeData.items.map((item: any) => ({
        name: item.name,
        explanation: item.description || `Menu item from ${restaurantName}`,
        acneImpactScore: 50,
        restaurantName: restaurantName,
      }));
      
      return {
        restaurantName,
        items: menuItems,
        source: 'backend_proxy',
        confidence: menuItems.length >= 10 ? 'high' : 'medium',
      };
    }
    
    return null;
  } catch (error) {
    console.error('Backend proxy error:', error);
    return null;
  }
}

/**
 * Main function to search for menu using all available API services
 */
export async function searchMenuApis(
  restaurantName: string,
  location?: string
): Promise<MenuApiResult | null> {
  // Try Yelp first
  const yelpResult = await searchYelpMenu(restaurantName, location);
  if (yelpResult) {
    return yelpResult;
  }
  
  // Try backend proxy scraping
  const proxyResult = await scrapeViaBackendProxy(restaurantName);
  if (proxyResult) {
    return proxyResult;
  }
  
  return null;
}

/**
 * Check if menu API services are available
 */
export function areMenuApisAvailable(): boolean {
  return !!(
    process.env.REACT_APP_YELP_API_KEY ||
    process.env.REACT_APP_BACKEND_URL
  );
}



